import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { 
  Grid, 
  Typography, 
  Box,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  FormControlLabel,
  FormHelperText
} from '@mui/material';
import { useRegistration } from '../../../context/RegistrationContext';

// Validation schema
const validationSchema = Yup.object({
  timezone: Yup.string().required('Timezone is required'),
  availability: Yup.object().test(
    'at-least-one-slot',
    'You must select at least one time slot',
    (value) => {
      // Check if at least one day has at least one time slot selected
      return Object.values(value).some(day => 
        Object.values(day).some(slot => slot === true)
      );
    }
  )
});

// Common timezones
const timezones = [
  { value: 'Asia/Kolkata', label: '(GMT+05:30) India Standard Time' },
  { value: 'America/New_York', label: '(GMT-04:00) Eastern Time' },
  { value: 'America/Chicago', label: '(GMT-05:00) Central Time' },
  { value: 'America/Denver', label: '(GMT-06:00) Mountain Time' },
  { value: 'America/Los_Angeles', label: '(GMT-07:00) Pacific Time' },
  { value: 'Europe/London', label: '(GMT+01:00) British Time' },
  { value: 'Europe/Berlin', label: '(GMT+02:00) Central European Time' },
  { value: 'Asia/Dubai', label: '(GMT+04:00) Gulf Standard Time' },
  { value: 'Asia/Singapore', label: '(GMT+08:00) Singapore Time' },
  { value: 'Australia/Sydney', label: '(GMT+10:00) Australian Eastern Time' }
];

// Time slots
const timeSlots = {
  morning: '9:00 AM - 12:00 PM',
  afternoon: '12:00 PM - 5:00 PM',
  evening: '5:00 PM - 9:00 PM'
};

// Days of the week
const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Initialize default availability
const initializeAvailability = () => {
  const defaultAvailability = {};
  
  daysOfWeek.forEach(day => {
    defaultAvailability[day] = {
      morning: false,
      afternoon: false,
      evening: false
    };
  });
  
  return defaultAvailability;
};

const AvailabilityStep = () => {
  const { formData, updateFormData, setStepValidity } = useRegistration();
  const [hasSelectedSlot, setHasSelectedSlot] = useState(false);
  
  // Initialize formik
  const formik = useFormik({
    initialValues: {
      timezone: formData.availability.timezone || 'Asia/Kolkata',
      availability: formData.availability.availability || initializeAvailability()
    },
    validationSchema,
    onSubmit: (values) => {
      // This step uses automatic validations and data saving
    },
    validateOnMount: true
  });
  
  // Update context when values change
  useEffect(() => {
    updateFormData('availability', formik.values);
  }, [formik.values, updateFormData]);
  
  // Update form validity when there are changes
  useEffect(() => {
    setStepValidity('availability', formik.isValid);
  }, [formik.isValid, setStepValidity]);
  
  // Check if at least one slot is selected
  useEffect(() => {
    const hasSlot = Object.values(formik.values.availability).some(day => 
      Object.values(day).some(slot => slot === true)
    );
    setHasSelectedSlot(hasSlot);
  }, [formik.values.availability]);
  
  // Handle checkbox changes
  const handleCheckboxChange = (day, slot) => {
    const newAvailability = { ...formik.values.availability };
    newAvailability[day][slot] = !newAvailability[day][slot];
    formik.setFieldValue('availability', newAvailability);
  };
  
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Set Your Availability
      </Typography>
      
      <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Select your regular availability for client consultations.
          You can update your schedule anytime from your dashboard.
          Make sure to select at least one time slot.
        </Typography>
      </Paper>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="timezone-label">Timezone</InputLabel>
            <Select
              labelId="timezone-label"
              id="timezone"
              name="timezone"
              value={formik.values.timezone}
              onChange={formik.handleChange}
              label="Timezone"
              error={formik.touched.timezone && Boolean(formik.errors.timezone)}
            >
              {timezones.map((timezone) => (
                <MenuItem key={timezone.value} value={timezone.value}>
                  {timezone.label}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.timezone && formik.errors.timezone && (
              <FormHelperText error>{formik.errors.timezone}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            Weekly Schedule
          </Typography>
          
          <Paper sx={{ p: 2, overflow: 'auto' }}>
            <Box sx={{ display: 'table', width: '100%' }}>
              {/* Table Header */}
              <Box sx={{ display: 'table-row' }}>
                <Box sx={{ display: 'table-cell', p: 1, fontWeight: 'bold' }}>Day</Box>
                {Object.entries(timeSlots).map(([slot, label]) => (
                  <Box key={slot} sx={{ display: 'table-cell', p: 1, fontWeight: 'bold', textAlign: 'center' }}>
                    {label}
                  </Box>
                ))}
              </Box>
              
              {/* Table Body */}
              {daysOfWeek.map((day) => (
                <Box key={day} sx={{ display: 'table-row' }}>
                  <Box sx={{ display: 'table-cell', p: 1, borderTop: '1px solid #eee' }}>{day}</Box>
                  
                  {Object.keys(timeSlots).map((slot) => (
                    <Box key={slot} sx={{ display: 'table-cell', p: 1, textAlign: 'center', borderTop: '1px solid #eee' }}>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formik.values.availability[day][slot]}
                              onChange={() => handleCheckboxChange(day, slot)}
                              name={`availability.${day}.${slot}`}
                            />
                          }
                          label=""
                          labelPlacement="end"
                        />
                      </FormGroup>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Paper>
          
          {!hasSelectedSlot && (
            <FormHelperText error>
              Please select at least one time slot
            </FormHelperText>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default AvailabilityStep;
